import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Row, Col, Card, Form } from 'react-bootstrap';
import AwpIsExpanded from '@components/Common/AwpIsExpanded';
import { Dropdown } from 'react-bootstrap';
import AwpSpinner from '@components/Common/AwpSpinner';
import Segment from './Segment';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import CreateSegmentModal from './CreateSegmentModal';
import SensorPlacementInfo from './SensorPlacementInfo';
import SensorPlacementEdit from './SensorPlacementEdit';
import PlacementMetaBoxInfo from './PlacementMetaBoxInfo';
import PlacementMetaBoxEdit from './PlacementMetaBoxEdit';
import SoilProfile from './SoilProfile/SoilProfile';
import EditableSoilProfile from './SoilProfile/EditSoilProfile';
import { isJsonValueEmpty, isInt, isValueEmpty } from '@awp/helpers/utilsReact';
import Authorize from '@awp/components/Authorize';
import { SENSOR_PLACEMENT_CONFIG, Permissions, STATE_STATUS } from '@awp/constants';
import {
  isEditing,
  setEditing,
  setPlacement,
  setOrignPlacement,
  savePlacement,
  getCurrentSensorEquipmentPacket,
  getSelectedPlacement,
  getOrignPlacement,
  getSaveStatus,
  getSaveError,
  getDeletePlacementStatus,
  getSensorTypes,
  getjsonObject,
  deletePlacement,
  setSaveStatus,
  setSaveError,
  setDeletePlacementStatus,
  setDeletePlacementError,
  setData,
} from '@stores/sensorEquipmentPacketSlice';

const checked = <i className="bi bi-check-square text-success mr-2" />;
const unchecked = <i className="bi bi-square mr-2" />;

const txtFontSize = {
  fontSize: '90%',
};

//a blank small column
const blankColumn = {
  maxWidth: '2rem',
};
const btnDelStyle = {
  fontSize: '80%',
};
const btnStyle = {
  marginRight: '0.2rem',
  marginBottom: '0.25rem',
  fontSize: '80%',
  padding: '0.4rem',
};

const Placement = ({ placement }) => {
  const dispatch = useDispatch();
  const inEditing = useSelector(isEditing);
  const currentSensorEquipmentPacket = useSelector(getCurrentSensorEquipmentPacket);
  const selectedPlacement = useSelector(getSelectedPlacement);
  const selectedOrignPlacement = useSelector(getOrignPlacement);
  const isSaveStatus = useSelector(getSaveStatus);
  const saveError = useSelector(getSaveError);
  const isDeletedPlacement = useSelector(getDeletePlacementStatus);
  const sensorTypeList = useSelector(getSensorTypes);

  const [createSegModal, setCreateSegModal] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const [saveTrigger, setSaveTrigger] = useState(false);
  const [showDeletePlacementConfirm, setShowDeletePlacementConfirm] = useState(false);
  const [layers, setLayers] = useState([]);

  const jsonObj = getjsonObject(placement, sensorTypeList);

  const isExpanded = () => {
    let _selectedPlacement = { ...placement };
    let _data = { ...currentSensorEquipmentPacket };
    let _sensorEquipmentSets = _data.SensorEquipmentSets;
    let _newSensorEquipmentSets = [];
    _sensorEquipmentSets.map((sp) => {
      let _sp = { ...sp };
      if (_sp.SensorPlacementId === _selectedPlacement.SensorPlacementId) {
        _sp.IsCollapsed = _selectedPlacement.IsCollapsed === 2 ? 1 : 2;
      }

      _newSensorEquipmentSets.push(_sp);
    });
    _data.SensorEquipmentSets = _newSensorEquipmentSets;
    dispatch(setData(_data));
  };

  const isShowInactive = () => {
    setShowInactive(!showInactive);
  };
  const handleSegModalClosed = useCallback(() => {
    setCreateSegModal(false);
  }, []);

  const isOtherEditing = () => {
    return (
      !isValueEmpty(inEditing) && inEditing !== SENSOR_PLACEMENT_CONFIG.PLACEMENT_TAG + placement.SensorPlacementId
    );
  };
  const isCurrentEditing = () => {
    return (
      !isValueEmpty(inEditing) && inEditing === SENSOR_PLACEMENT_CONFIG.PLACEMENT_TAG + placement.SensorPlacementId
    );
  };
  const enableEdit = () => {
    if (isOtherEditing() || isCurrentEditing()) return;
    setShowDeletePlacementConfirm(false);
    dispatch(setEditing(SENSOR_PLACEMENT_CONFIG.PLACEMENT_TAG + placement.SensorPlacementId));
    dispatch(setOrignPlacement(placement));
    dispatch(setPlacement(placement));
  };
  const updatePlacementName = (e) => {
    e.preventDefault();

    let _selectedPlacement = { ...selectedPlacement };
    _selectedPlacement.PlacementName = e.target.value;
    dispatch(setPlacement(_selectedPlacement));
  };
  const switchViewMode = () => {
    cleanData();
  };
  const cleanData = () => {
    dispatch(setEditing(null));
    dispatch(setOrignPlacement(null));
    dispatch(setPlacement(null));
    dispatch(setSaveError(null));
  };

  const getError = (field) => {
    if (isJsonValueEmpty(saveError)) return '';
    let err = { ...saveError };
    //check property of dict obj
    field = (field + '').toLocaleLowerCase();
    for (let key in err) {
      let k = key.toLocaleLowerCase();
      if (!isValueEmpty(key) && k.startsWith(field)) {
        let id = k.replace(field + '.', '');
        if (isInt(id)) {
          if (placement.SensorPlacementId + '' === id + '') {
            return err[key];
          }
        }
      }
    }
    return '';
  };

  const handleCancel = () => {
    if (selectedPlacement.SensorPlacementId === SENSOR_PLACEMENT_CONFIG.ID) {
      let _data = { ...currentSensorEquipmentPacket };
      let _sensorEquipmentSets = [..._data.SensorEquipmentSets];
      let _newSensorEquipmentSets = [];
      _sensorEquipmentSets.map((sp) => {
        if (sp.SensorPlacementId !== SENSOR_PLACEMENT_CONFIG.ID) _newSensorEquipmentSets.push(sp);
      });

      _data.SensorEquipmentSets = _newSensorEquipmentSets;
      dispatch(setData(_data));
    }
    switchViewMode();
    setPlacement({ ...selectedOrignPlacement });
  };
  const handlePlacementSubmit = () => {
    setSaveTrigger(true);
    dispatch(setSaveError(null));
    setSaveStatus(STATE_STATUS.LOADING);
    const _selectedPlacement = { ...selectedPlacement };
    _selectedPlacement.Longitude = isValueEmpty(selectedPlacement.Longitude) ? null : selectedPlacement.Longitude;
    _selectedPlacement.Latitude = isValueEmpty(selectedPlacement.Latitude) ? null : selectedPlacement.Latitude;
    dispatch(setPlacement({ ..._selectedPlacement }));
    dispatch(savePlacement());
  };
  const deleteSelectedPlacement = () => {
    let _selectedPlacement = { ...selectedPlacement };
    dispatch(setDeletePlacementError(null));
    setDeletePlacementStatus(STATE_STATUS.LOADING);
    dispatch(setPlacement(_selectedPlacement));
    dispatch(deletePlacement());
    setShowDeletePlacementConfirm(false);
  };
  const switchCollapsed = () => {
    return getCollapsed();
  };
  const getCollapsed = () => {
    return placement.IsCollapsed === 2 ? false : true;
  };
  const onClickDeletePlacement = () => {
    setShowDeletePlacementConfirm(true);
  };
  const isNewPlacement = () => {
    let _selectedPlacement = { ...selectedPlacement };
    if (!_selectedPlacement || _selectedPlacement.SensorPlacementId < 0) return true;
    return false;
  };
  const setSaveButtonDisabled = (result) => {
    setDisableSave(result);
  };
  const onSaveChanges = () => {
    setSaveTrigger(false);
  };

  return (
    <Card style={{ marginTop: '0.2rem' }}>
      <Card.Body style={{ padding: '0.4rem' }}>
        <Row>
          <Col md={1} style={blankColumn}>
            <AwpIsExpanded collapsed={switchCollapsed()} onClick={isExpanded} />
          </Col>
          <Col md={4} style={{ maxWidth: '70%' }}>
            {!isCurrentEditing() && placement && <div style={txtFontSize}>{placement.PlacementName}</div>}
            {isCurrentEditing() && selectedPlacement && (
              <Form.Control
                onChange={updatePlacementName}
                className="text-size90"
                style={{ marginBottom: '0.6rem' }}
                type="text"
                maxLength="50"
                value={isValueEmpty(selectedPlacement.PlacementName) ? '' : selectedPlacement.PlacementName}
              />
            )}
            {!isValueEmpty(getError('sensorequipmentset.PlacementName')) && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {getError('sensorequipmentset.PlacementName')}
              </Form.Control.Feedback>
            )}
          </Col>
        </Row>
        {!getCollapsed() && (
          <>
            <Row>
              {!isCurrentEditing() && <SensorPlacementInfo placement={placement} />}
              {isCurrentEditing() && <SensorPlacementEdit placement={placement} />}
              <Col md={3}>
                {!isCurrentEditing() && (
                  <Dropdown className="d-flex" style={{ justifyContent: 'flex-end' }}>
                    <Dropdown.Toggle variant="primary" id="dropdown_actions_id">
                      Actions
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Authorize requires={[Permissions.WriteSensorPlacement]}>
                        <Dropdown.Item onClick={() => setCreateSegModal(!createSegModal)} disabled={isOtherEditing()}>
                          Create Segment
                        </Dropdown.Item>
                        <Dropdown.Item onClick={enableEdit} disabled={isOtherEditing()}>
                          Edit Placement
                        </Dropdown.Item>
                      </Authorize>
                      <Dropdown.Item onClick={isShowInactive}>
                        {showInactive ? checked : unchecked}Show Inactive
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}
                {
                  <Authorize requires={[Permissions.WriteSensorPlacement]}>
                    {isCurrentEditing() && (
                      <div>
                        {isSaveStatus !== STATE_STATUS.LOADING && isDeletedPlacement !== STATE_STATUS.LOADING && (
                          <div>
                            {!showDeletePlacementConfirm && (
                              <div style={{ width: '12rem' }}>
                                <Button
                                  variant="primary"
                                  //className="align-self-center form-control-lg"
                                  disabled={disableSave}
                                  style={btnStyle}
                                  onClick={handlePlacementSubmit}
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="secondary"
                                  //className="align-self-center form-control-lg"
                                  style={btnStyle}
                                  onClick={handleCancel}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  variant="danger"
                                  //className="align-self-center form-control-lg"
                                  style={btnStyle}
                                  onClick={onClickDeletePlacement}
                                  disabled={isNewPlacement()}
                                >
                                  Delete
                                </Button>
                              </div>
                            )}
                            {showDeletePlacementConfirm && (
                              <div style={{ width: '10rem' }}>
                                <AwpYesNoDialog
                                  action={deleteSelectedPlacement}
                                  msg="Delete placement?"
                                  setShowYesNoConfirm={setShowDeletePlacementConfirm}
                                  props={{ style: btnDelStyle }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                        {(isSaveStatus == STATE_STATUS.LOADING || isDeletedPlacement == STATE_STATUS.LOADING) && (
                          <AwpSpinner />
                        )}
                      </div>
                    )}
                  </Authorize>
                }
              </Col>
            </Row>
            <Row>
              {!isCurrentEditing() && placement && jsonObj && (
                <PlacementMetaBoxInfo
                  key={'pmbox_key_' + placement.SensorPlacementId + ''}
                  placementId={placement.SensorPlacementId}
                  jsonObj={jsonObj}
                />
              )}
            </Row>
            <Row>
              {isCurrentEditing() && selectedPlacement && (
                <PlacementMetaBoxEdit
                  key={'pmbox_key_' + selectedPlacement.SensorPlacementId + ''}
                  placementId={selectedPlacement.SensorPlacementId}
                />
              )}
            </Row>
            {!isCurrentEditing() && <SoilProfile placement={placement} />}
            {isCurrentEditing() && selectedPlacement && (
              <EditableSoilProfile
                onValidationChange={(isValid) => setSaveButtonDisabled(!isValid)}
                saveTrigger={saveTrigger}
                onSaveChanges={onSaveChanges}
                layers={layers}
                setLayers={setLayers}
              />
            )}
            <Row>
              <Col md={12}>
                <Card>
                  <Card.Header style={{ padding: '1rem 1.25rem' }}>
                    <h4>Data Segments</h4>
                  </Card.Header>
                  {placement && placement.PlacementSegments && (
                    <Card.Body style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                      {placement.PlacementSegments.map((segment, i) => {
                        return (
                          (isValueEmpty(segment.SegmentEndTimestamp) || showInactive) && (
                            <div key={'seg_body_bar_key_' + i + ''}>
                              <Segment key={'seg_key_' + i + ''} segment={segment} />
                              <div key={'seg_bottom_bar_key_' + i + ''} style={{ height: '1rem' }}></div>
                            </div>
                          )
                        );
                      })}
                    </Card.Body>
                  )}
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Card.Body>
      <CreateSegmentModal placement={placement} visible={createSegModal} closeModal={handleSegModalClosed} />
    </Card>
  );
};

Placement.propTypes = {
  placement: PropTypes.any.isRequired,
};

export default Placement;
